import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useMeta from "@/hooks/useMeta";
import { Card, Button, Input, Select, Label, Modal, Spinner, EmptyState, Avatar, PriorityBadge, TimelineBadge } from "@/components/UI";
import TaskModal from "@/components/TaskModal";
import { BellRing, Check, X, Plus, AlertTriangle } from "lucide-react";

export default function Approvals() {
  const { isAdmin } = useAuth();
  const meta = useMeta();
  const [tasks, setTasks] = useState(null);
  const [approveModal, setApproveModal] = useState({ open: false, task: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [weight, setWeight] = useState("");
  const [sprintId, setSprintId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tasks?status=waiting_approval").then((r) => setTasks(r.data));
  useEffect(() => { load(); }, []);

  const isInternshipTask = (task) => task?.assignee?.role === "internship";

  const openApprove = (task) => {
    const active = meta.sprints?.find((s) => s.status === "active");
    setWeight(isInternshipTask(task) ? "0" : task.weight != null ? String(task.weight) : "");
    setSprintId(task.sprint_id ? String(task.sprint_id) : active ? String(active.id) : "");
    setApproveModal({ open: true, task });
  };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/tasks/${approveModal.task.id}/approve`, {
        weight: isInternshipTask(approveModal.task) ? 0 : Number(weight),
        sprint_id: sprintId || null,
        status: "to_do",
      });
      toast.success("Task di-approve & bobot ditetapkan");
      setApproveModal({ open: false, task: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal approve");
    } finally { setBusy(false); }
  };

  const reject = async (task) => {
    try {
      await api.post(`/tasks/${task.id}/reject`, { reason: "Ditolak oleh admin" });
      toast.success("Task ditolak");
      load();
    } catch (e) { toast.error("Gagal"); }
  };

  if (!tasks) return <Spinner />;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="approvals-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Task Approval</h1>
          <p className="text-sm text-slate-400">
            {isAdmin ? "Urgent task & permintaan masuk Kanban yang menunggu bobot/sprint." : "Buat urgent task atau ajukan task dari Backlog ke Kanban — menunggu Admin/PM."}
          </p>
        </div>
        {!isAdmin && (
          <Button onClick={() => setCreateOpen(true)} data-testid="create-urgent-button"><Plus size={16} /> Buat Urgent Task</Button>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-slate-500/30 bg-slate-500/10 p-3 text-xs text-slate-200">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <span>Employee dapat membuat urgent task, namun <b>bobot pekerjaan hanya boleh ditentukan/approved oleh Head/PM/Super Admin</b>. Setelah approval, task masuk perhitungan sprint & performance.</span>
      </div>

      {tasks.length === 0 ? (
        <EmptyState icon={BellRing} title="Tidak ada task menunggu approval" subtitle="Semua urgent task & permintaan Kanban sudah diproses." />
      ) : (
        <div className="space-y-3">
          {tasks.map((t) => (
            <Card key={t.id} className="p-4" data-testid={`approval-card-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <span className="rounded-md bg-rose-500/15 px-2 py-0.5 text-[10px] font-bold text-rose-400">WAITING APPROVAL</span>
                    <PriorityBadge priority={t.priority} />
                    <TimelineBadge date={t.deadline} />
                  </div>
                  <p className="font-heading font-semibold text-slate-100">{t.title}</p>
                  {t.description && <p className="mt-0.5 text-sm text-slate-400">{t.description}</p>}
                  {t.urgency_reason && <p className="mt-1 text-xs italic text-slate-300/80">Alasan: {t.urgency_reason}</p>}
                  <div className="mt-2 flex items-center gap-2">
                    <Avatar src={t.assignee?.avatar_url} name={t.assignee?.name || "?"} size={22} />
                    <span className="text-xs text-slate-400">PIC: {t.assignee?.name || "—"} · Project: {t.project?.name || "—"}</span>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button variant="success" size="sm" onClick={() => openApprove(t)} data-testid={`approve-btn-${t.id}`}><Check size={14} /> Approve</Button>
                    <Button variant="danger" size="sm" onClick={() => reject(t)} data-testid={`reject-btn-${t.id}`}><X size={14} /> Tolak</Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Approve modal */}
      <Modal open={approveModal.open} onClose={() => setApproveModal({ open: false, task: null })} title="Approve & Tetapkan Bobot" size="sm"
        footer={<><Button variant="ghost" onClick={() => setApproveModal({ open: false, task: null })}>Batal</Button>
          <Button variant="success" onClick={approve} disabled={busy || (!isInternshipTask(approveModal.task) && !weight)} data-testid="confirm-approve">{busy ? "..." : "Approve"}</Button></>}>
        <div className="space-y-4">
          <p className="text-sm text-slate-300">{approveModal.task?.title}</p>
          {isInternshipTask(approveModal.task) ? (
            <div className="rounded-lg border border-slate-500/30 bg-slate-500/10 p-3 text-xs text-slate-200">
              PIC task ini adalah <b>Internship</b> — bobot otomatis <b>0 pts</b>, tidak perlu diisi manual.
            </div>
          ) : (
            <div>
              <Label className="text-slate-400">Bobot Resmi (pts)</Label>
              <Input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="mis. 8" data-testid="approve-weight-input" />
            </div>
          )}
          <div>
            <Label>Masukkan ke Sprint</Label>
            <Select value={sprintId} onChange={(e) => setSprintId(e.target.value)} data-testid="approve-sprint-select">
              <option value="">— Sprint saat ini (tetap) —</option>
              {meta.sprints?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </div>
        </div>
      </Modal>

      <TaskModal open={createOpen} mode="create" task={null} meta={meta} onClose={() => setCreateOpen(false)} onSaved={load} />
    </div>
  );
}
